import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useOfferWorkspace, useSaveOfferWorkspace, type OfferWorkspacePatch } from "@/hooks/use-offer-workspace";
import { parseAiSources } from "@/lib/tilbudsarbeidsrom/ai-sources";
import { parseKroner } from "@/lib/freelancers";
import type { Offer } from "@/hooks/use-offers";
import { AiSourcesPanel } from "./AiSourcesPanel";

interface GrunnlagBranchProps {
  offer: Offer;
  customerLabel: string;
  description: string;
  onDescriptionChange: (next: string) => void;
  readOnly: boolean;
}

const formatFrame = (n: number | null | undefined) =>
  n == null ? "" : new Intl.NumberFormat("nb-NO", { maximumFractionDigits: 0 }).format(n);

/**
 * Grenen Grunnlag (VIB-2979). Kort beskrivelse er tilbudets eget felt og lagres
 * med «Lagre» i headeren. Målgruppe, ramme og AI-kilder lagres når feltet
 * forlates eller en kilde krysses av.
 */
export function GrunnlagBranch({ offer, customerLabel, description, onDescriptionChange, readOnly }: GrunnlagBranchProps) {
  const { data: workspace, isLoading } = useOfferWorkspace(offer.id);
  const save = useSaveOfferWorkspace(offer.id);

  const [audience, setAudience] = useState("");
  const [frame, setFrame] = useState("");
  useEffect(() => setAudience(workspace?.target_audience ?? ""), [workspace?.target_audience]);
  useEffect(() => setFrame(formatFrame(workspace?.budget_frame_nok)), [workspace?.budget_frame_nok]);

  // Lagringene går i kø med versjonen forrige lagring returnerte. Ellers
  // bruker to raske lagringer (målgruppe, så ramme) samme gamle versjon,
  // og den siste avvises som konflikt.
  const version = useRef<string | null>(null);
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  useEffect(() => {
    version.current = workspace?.updated_at ?? null;
  }, [workspace?.updated_at]);

  const persist = (patch: OfferWorkspacePatch) => {
    queue.current = queue.current
      .then(() => save.mutateAsync({ patch, expectedUpdatedAt: version.current }))
      .then((updatedAt) => {
        version.current = updatedAt;
      })
      .catch(() => {
        // Feilen er vist som toast av hooken; neste lagring bruker hentet versjon.
      });
  };

  function commitAudience() {
    const next = audience.trim() || null;
    if (next !== (workspace?.target_audience ?? null)) persist({ target_audience: next });
  }

  function commitFrame() {
    const parsed = parseKroner(frame);
    if (parsed !== (workspace?.budget_frame_nok ?? null)) persist({ budget_frame_nok: parsed });
    setFrame(formatFrame(parsed));
  }

  const disabled = readOnly || isLoading;

  return (
    <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_360px]">
      <section className="flex flex-col gap-5 max-w-3xl" aria-labelledby="grunnlag-heading">
        <div>
          <h2 id="grunnlag-heading" className="font-display text-xl">Grunnlag</h2>
          <p className="text-sm text-muted-foreground">
            Det minste som trengs for å komme i gang. Resten kan fylles ut underveis.
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">Kunde</span>
          <span className="text-sm">{customerLabel}</span>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="grunnlag-description">Kort beskrivelse</Label>
          <Textarea
            id="grunnlag-description"
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            readOnly={readOnly}
            rows={5}
            placeholder="Hva skal lages, for hvem og hvorfor?"
          />
          <span className="text-xs text-muted-foreground">Lagres med «Lagre» øverst, sammen med tittelen.</span>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="grunnlag-audience">Målgruppe</Label>
            <Input
              id="grunnlag-audience"
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              onBlur={commitAudience}
              disabled={disabled}
              placeholder="Hvem skal se filmen?"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="grunnlag-frame">Budsjettramme (kr eks. mva)</Label>
            <Input
              id="grunnlag-frame"
              inputMode="numeric"
              value={frame}
              onChange={(e) => setFrame(e.target.value)}
              onBlur={commitFrame}
              disabled={disabled}
              placeholder="150 000"
            />
          </div>
        </div>
        <span className="text-xs text-muted-foreground" role="status">
          {save.isPending ? "Lagrer …" : "Målgruppe, ramme og kilder lagres med en gang."}
        </span>
      </section>

      <AiSourcesPanel
        customerId={offer.customer_id}
        choices={parseAiSources(workspace?.ai_sources)}
        readOnly={disabled}
        saving={save.isPending}
        onChange={(next) => persist({ ai_sources: next })}
      />
    </div>
  );
}
